/**
 * PersonaWorkshopPage - Primary persona discovery entry point
 *
 * Streamlined landing with persona type selection, action buttons,
 * and a stakeholder map. Personas can reveal inline, resume discovery,
 * or open the full detail editor.
 */

import { Fragment, useState, useCallback, useEffect, useRef, useMemo } from 'react'
import {
  ArrowRight,
  ArrowUpRight,
  ChevronDown,
  FileInput,
  Filter,
  Plus,
  RefreshCw,
  Search,
  Sparkles,
  Brain,
  Target,
  Trash2,
  X,
} from 'lucide-react'
import { usePersonaWorkshop } from '@/hooks/usePersonaWorkshop'
import { usePersonasForCanvas } from '@/hooks/usePersonasForCanvas'
import { useEntityCrudListener } from '@/hooks/useEntityListener'
import { useAIStore } from '@/stores/aiStore'
import { useSkerpaContextStore } from '@/stores/skerpaContextStore'
import { marketSegmentationApi, personaWorkshopApi } from '@/lib/api/client'
import { PersonaWorkshopSession } from './PersonaWorkshopSession'
import { ImportPersonaDialog } from '@/components/persona-configurator/ImportPersonaDialog'
import { PersonaCardDetail } from '@/components/persona-configurator/PersonaCardDetail'
import { migratePersonaData } from '@ispot/shared'
import type {
  JtbdData,
  MarketPersona,
  MarketSegmentWithScore,
  PersonaData,
  PersonaType,
  PersonaWorkshopSession as SessionType,
  SegmentPersonaLink,
} from '@ispot/shared'
import type { EntityTreeInstance } from '@/lib/api/client'

interface PersonaWorkshopPageProps {
  accountId: string
  projectId: string
}

type ActiveMode =
  | { kind: 'list' }
  | { kind: 'workshop'; sessionId: string; targetJtbd?: number | 'new' }
  | { kind: 'edit'; sessionId: string }

type StakeholderTypeFilter = 'all' | PersonaType

interface SegmentInfo {
  id: string
  name: string
  coverage?: SegmentPersonaLink['coverage']
}

function completenessLabel(data: PersonaData): string {
  const d = migratePersonaData(data)
  let filled = 0
  if (d.name) filled++
  if (d.role) filled++
  if (d.jtbds?.length > 0) filled++
  const hasPainsOrGains = d.jtbds?.some(j => j.pains.length > 0 || j.gains.length > 0)
  if (hasPainsOrGains) filled++
  if (filled === 4) return 'Complete'
  if (filled >= 2) return 'In progress'
  return 'Started'
}

function completenessScore(data: PersonaData): number {
  const d = migratePersonaData(data)
  let filled = 0
  const total = 4
  if (d.name) filled++
  if (d.role) filled++
  if (d.jtbds?.length > 0) filled++
  const hasPainsOrGains = d.jtbds?.some(j => j.pains.length > 0 || j.gains.length > 0)
  if (hasPainsOrGains) filled++
  return Math.round((filled / total) * 100)
}

function totalPains(jtbds: JtbdData[]): number {
  return jtbds.reduce((sum, j) => sum + j.pains.length, 0)
}

function totalGains(jtbds: JtbdData[]): number {
  return jtbds.reduce((sum, j) => sum + j.gains.length, 0)
}

function timeAgo(dateStr: string): string {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return days === 1 ? 'yesterday' : `${days}d ago`
}

function computeInitials(data: PersonaData): string {
  if (data.name) {
    return data.name
      .split(' ')
      .map(w => w[0])
      .join('')
      .slice(0, 2)
      .toUpperCase()
  }
  if (data.role) return data.role[0].toUpperCase()
  return '?'
}

function primaryJtbd(data: PersonaData): JtbdData | undefined {
  return data.jtbds?.[0]
}

function findMarketPersona(session: SessionType, marketPersonas: MarketPersona[]): MarketPersona | undefined {
  const data = migratePersonaData(session.personaData as PersonaData)
  const normalizedName = data.name.trim().toLowerCase()
  return marketPersonas.find(p => p.linkedWorkshopPersonaId === session.id)
    || (normalizedName ? marketPersonas.find(p => p.name.trim().toLowerCase() === normalizedName) : undefined)
}

function getSessionSegments(
  session: SessionType,
  marketPersonas: MarketPersona[],
  segments: MarketSegmentWithScore[],
  coverage: SegmentPersonaLink[],
): SegmentInfo[] {
  const marketPersona = findMarketPersona(session, marketPersonas)
  const linkedSegments = marketPersona
    ? coverage
        .filter(link => link.personaId === marketPersona.id)
        .map(link => {
          const segment = segments.find(s => s.id === link.segmentId)
          return segment ? { id: segment.id, name: segment.name, coverage: link.coverage } : null
        })
        .filter((segment): segment is SegmentInfo => segment !== null)
    : []

  if (linkedSegments.length > 0) return linkedSegments

  const data = migratePersonaData(session.personaData as PersonaData)
  if (data.segment?.trim()) {
    return [{ id: `legacy:${data.segment.trim().toLowerCase()}`, name: data.segment.trim() }]
  }
  return []
}

function typeFilterLabel(filter: StakeholderTypeFilter): string {
  if (filter === 'all') return 'All'
  return filter === 'buyer' ? 'Buyer' : 'User'
}

function typeBadge(session: SessionType): string {
  return session.personaType === 'buyer' ? 'Buyer' : 'User'
}

function severityTone(severity?: string): string {
  if (severity === 'high') return 'bg-error'
  if (severity === 'medium') return 'bg-solution'
  return 'bg-warm-300'
}

function severityWidth(severity?: string): string {
  if (severity === 'high') return '88%'
  if (severity === 'medium') return '62%'
  return '38%'
}

function importanceTone(importance?: string): string {
  if (importance === 'must_have') return 'bg-market'
  if (importance === 'expected') return 'bg-business'
  return 'bg-warm-300'
}

function importanceWidth(importance?: string): string {
  if (importance === 'must_have') return '88%'
  if (importance === 'expected') return '62%'
  return '38%'
}

export function PersonaWorkshopPage({ accountId, projectId }: PersonaWorkshopPageProps) {
  // Persona type now picked inside the wizard's identity phase, not at the
  // page-header level. Default to 'user' for new sessions; the wizard updates
  // session.personaType when the user switches.
  const defaultPersonaType: PersonaType = 'user'
  const [mode, setMode] = useState<ActiveMode>({ kind: 'list' })
  const [expandedSessionId, setExpandedSessionId] = useState<string | null>(null)
  const [isSuggesting, setIsSuggesting] = useState(false)
  const [showImport, setShowImport] = useState(false)
  const [suggestError, setSuggestError] = useState<string | null>(null)
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set())
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle')
  const [typeFilter, setTypeFilter] = useState<StakeholderTypeFilter>('all')
  const [segmentFilter, setSegmentFilter] = useState('all')
  const [marketPersonas, setMarketPersonas] = useState<MarketPersona[]>([])
  const [marketSegments, setMarketSegments] = useState<MarketSegmentWithScore[]>([])
  const [segmentCoverage, setSegmentCoverage] = useState<SegmentPersonaLink[]>([])
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingDataRef = useRef<PersonaData | null>(null)

  const workshop = usePersonaWorkshop(accountId, projectId)
  const { personas: graphPersonas, isLoading: graphLoading } = usePersonasForCanvas()
  const { apiKey } = useAIStore()
  const [syncingIds, setSyncingIds] = useState<Set<string>>(new Set())
  const pendingOpenSessionId = useSkerpaContextStore(s => s.pendingOpenSessionId)
  const setPendingOpenSession = useSkerpaContextStore(s => s.setPendingOpenSession)
  const setActiveResource = useSkerpaContextStore(s => s.setActiveResource)

  useEffect(() => {
    workshop.loadSessions()
  }, [workshop.loadSessions])

  // Keep Skerpa informed of the currently open session so JTBD/pain/gain
  // actions can write to the right persona without name-matching heuristics.
  useEffect(() => {
    setActiveResource(mode.kind === 'list' ? null : mode.sessionId)
  }, [mode, setActiveResource])

  // Auto-open a session created by Skerpa (e.g. after "Create persona" action)
  useEffect(() => {
    if (!pendingOpenSessionId) return
    workshop.loadSession(pendingOpenSessionId)
    setMode({ kind: 'workshop', sessionId: pendingOpenSessionId })
    setPendingOpenSession(null)
  }, [pendingOpenSessionId, workshop.loadSession, setPendingOpenSession])

  const loadStakeholderSegments = useCallback(async () => {
    try {
      const bundle = await marketSegmentationApi.getCanvas(accountId, projectId)
      setMarketPersonas(Array.isArray(bundle?.personas) ? bundle.personas : [])
      setMarketSegments(Array.isArray(bundle?.segments) ? bundle.segments : [])
      setSegmentCoverage(Array.isArray(bundle?.coverage) ? bundle.coverage : [])
    } catch {
      setMarketPersonas([])
      setMarketSegments([])
      setSegmentCoverage([])
    }
  }, [accountId, projectId])

  useEffect(() => {
    loadStakeholderSegments()
  }, [loadStakeholderSegments])

  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current)
    }
  }, [])

  // Reload when Skerpa writes persona data (agent actions use local state, not TanStack Query)
  const refreshFromAgent = useCallback(() => {
    workshop.loadSessions()
    if (mode.kind !== 'list') workshop.loadSession(mode.sessionId)
  }, [workshop.loadSessions, workshop.loadSession, mode])
  useEntityCrudListener('Persona', refreshFromAgent)
  useEntityCrudListener('customerjob', refreshFromAgent)
  useEntityCrudListener('customerpain', refreshFromAgent)
  useEntityCrudListener('customergain', refreshFromAgent)

  const handleSync = useCallback(async (sessionId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    setSyncingIds(prev => new Set(prev).add(sessionId))
    try {
      await personaWorkshopApi.syncSession(accountId, projectId, sessionId)
      await workshop.loadSessions()
    } finally {
      setSyncingIds(prev => { const next = new Set(prev); next.delete(sessionId); return next })
    }
  }, [accountId, projectId, workshop.loadSessions])

  const handleDeletePersona = useCallback(async (sessionId: string) => {
    if (!window.confirm('Delete this persona? This cannot be undone.')) return
    setDeletingIds(prev => new Set(prev).add(sessionId))
    try {
      await personaWorkshopApi.deleteSession(accountId, projectId, sessionId)
      setExpandedSessionId(current => current === sessionId ? null : current)
      await workshop.loadSessions()
    } finally {
      setDeletingIds(prev => { const next = new Set(prev); next.delete(sessionId); return next })
    }
  }, [accountId, projectId, workshop.loadSessions])

  const handleStart = useCallback(async () => {
    const session = await workshop.createSession(defaultPersonaType, 'quick')
    if (session) setMode({ kind: 'workshop', sessionId: session.id })
  }, [workshop.createSession])

  const handleOpenGraphPersona = useCallback(async (entity: EntityTreeInstance) => {
    try {
      const session = await personaWorkshopApi.createSessionFromEntity(accountId, projectId, {
        entityUri: entity.uri,
        label: entity.label,
        personaType: 'user',
      }) as SessionType
      workshop.loadSession(session.id)
      setMode({ kind: 'workshop', sessionId: session.id })
    } catch {
      const session = await workshop.createSession('user', 'quick')
      if (session) setMode({ kind: 'workshop', sessionId: session.id })
    }
  }, [accountId, projectId, workshop])

  const handleSuggestFromIntelligence = useCallback(async () => {
    if (!apiKey) {
      setSuggestError('Add an AI API key in Settings to use this feature.')
      return
    }
    setSuggestError(null)
    setIsSuggesting(true)
    try {
      const session = await personaWorkshopApi.suggestFromIntelligence(accountId, projectId, {
        personaType: defaultPersonaType,
        apiKey,
      }) as SessionType
      workshop.loadSession(session.id)
      setMode({ kind: 'workshop', sessionId: session.id })
    } catch {
      setSuggestError('Could not generate suggestion. Try again.')
    } finally {
      setIsSuggesting(false)
    }
  }, [accountId, projectId, apiKey, workshop])

  const handleImportCreated = useCallback(async (session: SessionType) => {
    await workshop.loadSessions()
    await workshop.loadSession(session.id)
    setMode({ kind: 'workshop', sessionId: session.id })
  }, [workshop])

  const handleBack = useCallback(() => {
    setMode({ kind: 'list' })
    workshop.loadSessions()
  }, [workshop.loadSessions])

  const handleOpenSession = useCallback((s: SessionType) => {
    if (s.status === 'completed') {
      setMode({ kind: 'edit', sessionId: s.id })
      return
    }
    workshop.loadSession(s.id)
    setMode({ kind: 'workshop', sessionId: s.id })
  }, [workshop])

  const handleOpenEditor = useCallback((sessionId: string) => {
    setMode({ kind: 'edit', sessionId })
  }, [])

  // Debounced update for the detail editor
  const handleDetailUpdate = useCallback((sessionId: string, data: Partial<PersonaData>) => {
    // Optimistic local update
    workshop.sessions.forEach(() => {}) // force reference
    const session = workshop.sessions.find(s => s.id === sessionId)
    if (!session) return
    const current = migratePersonaData(session.personaData as PersonaData)
    const merged = { ...current, ...data } as PersonaData
    pendingDataRef.current = merged

    setSaveStatus('saving')
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current)
    saveTimerRef.current = setTimeout(async () => {
      try {
        const toSave = pendingDataRef.current || data
        await personaWorkshopApi.updateSession(accountId, projectId, sessionId, { personaData: toSave })
        await workshop.loadSessions()
        setSaveStatus('saved')
        savedTimerRef.current = setTimeout(() => setSaveStatus('idle'), 2000)
      } catch {
        setSaveStatus('idle')
      }
    }, 500)
  }, [accountId, projectId, workshop])

  const handleSynthesizeProblem = useCallback(async (sessionId: string, jtbdId: string) => {
    try {
      await personaWorkshopApi.synthesizeProblem(accountId, projectId, sessionId, { jtbdId, apiKey })
      await workshop.loadSessions()
    } catch { /* handled by UI */ }
  }, [accountId, projectId, apiKey, workshop])

  const visibleSessions = useMemo(
    () => workshop.sessions.filter(s => s.status !== 'abandoned'),
    [workshop.sessions],
  )

  const segmentOptions = useMemo(() => {
    const options = new Map<string, string>()
    marketSegments.forEach(segment => options.set(segment.id, segment.name))
    visibleSessions.forEach(session => {
      getSessionSegments(session as SessionType, marketPersonas, marketSegments, segmentCoverage).forEach(segment => {
        options.set(segment.id, segment.name)
      })
    })
    return Array.from(options.entries()).map(([id, name]) => ({ id, name }))
  }, [marketSegments, marketPersonas, segmentCoverage, visibleSessions])

  const filteredSessions = useMemo(() => {
    return visibleSessions.filter(session => {
      if (typeFilter !== 'all' && session.personaType !== typeFilter) return false
      if (segmentFilter === 'all') return true
      const applicableSegments = getSessionSegments(session as SessionType, marketPersonas, marketSegments, segmentCoverage)
      return applicableSegments.some(segment => segment.id === segmentFilter)
    })
  }, [marketPersonas, marketSegments, segmentCoverage, segmentFilter, typeFilter, visibleSessions])

  useEffect(() => {
    if (expandedSessionId && !filteredSessions.some(s => s.id === expandedSessionId)) {
      setExpandedSessionId(null)
    }
  }, [expandedSessionId, filteredSessions])

  // ── Workshop session view ───────────────────────────────────────────
  if (mode.kind === 'workshop' && workshop.session) {
    return (
      <PersonaWorkshopSession
        accountId={accountId}
        projectId={projectId}
        session={workshop.session}
        targetJtbd={mode.kind === 'workshop' ? mode.targetJtbd : undefined}
        onBack={handleBack}
      />
    )
  }

  // ── Detail editor view ──────────────────────────────────────────────
  if (mode.kind === 'edit') {
    const editSession = workshop.sessions.find(s => s.id === mode.sessionId)
    if (editSession) {
      return (
        <PersonaCardDetail
          session={editSession}
          accountId={accountId}
          projectId={projectId}
          onBack={handleBack}
          onUpdate={(data) => handleDetailUpdate(editSession.id, data)}
          onSynthesizeProblem={(jtbdId) => handleSynthesizeProblem(editSession.id, jtbdId)}
          onOpenWorkshop={(id, jtbdTarget) => { workshop.loadSession(id); setMode({ kind: 'workshop', sessionId: id, targetJtbd: jtbdTarget }) }}
        />
      )
    }
  }

  // ── Landing page ────────────────────────────────────────────────────
  const sessionUris = new Set(workshop.sessions.map(s => s.personaEntityUri).filter(Boolean))
  const seenUris = new Set<string>()
  const unlinkedGraphPersonas = graphPersonas.filter(p => {
    if (!p.uri || seenUris.has(p.uri) || sessionUris.has(p.uri)) return false
    seenUris.add(p.uri)
    return true
  })

  return (
    <div className="flex-1 overflow-y-auto px-8 py-7">
      <div className="max-w-[1040px] mx-auto">
        <header className="mb-6 flex flex-col gap-4 border-b border-warm-200 pb-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="font-display text-[24px] font-bold tracking-tight">Personas</h1>
              <span className="rounded-full bg-warm-100 px-2 py-0.5 text-[11px] font-medium text-text-muted">
                {visibleSessions.length} stakeholder{visibleSessions.length === 1 ? '' : 's'}
              </span>
            </div>
            <p className="mt-1 text-[14px] text-text-muted">
              Map who your product serves and open any persona for deeper JTBD work.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-1">
            <button
              onClick={handleStart}
              disabled={workshop.isLoading}
              className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[13px] font-medium text-white transition-colors disabled:opacity-50"
              style={{ backgroundColor: 'var(--color-accent, #e84300)' }}
              title="Start guided persona discovery"
            >
              <Plus size={13} />
              New persona
            </button>

            <button
              onClick={() => setShowImport(true)}
              disabled={workshop.isLoading}
              title="Import a persona from a text description or document"
              className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[13px] font-medium text-text-secondary transition-colors hover:bg-warm-100 hover:text-text disabled:opacity-50"
            >
              <FileInput size={13} />
              Import
            </button>

            <button
              onClick={handleSuggestFromIntelligence}
              disabled={isSuggesting || workshop.isLoading}
              title="AI drafts a persona from your product intelligence (interviews, docs, segments)"
              className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[13px] font-medium text-text-secondary transition-colors hover:bg-warm-100 hover:text-text disabled:opacity-50"
            >
              {isSuggesting ? (
                <RefreshCw size={13} className="animate-spin" />
              ) : (
                <Sparkles size={13} />
              )}
              {isSuggesting ? 'Analyzing…' : 'Suggest'}
            </button>
          </div>
        </header>

        {suggestError && (
          <p className="-mt-3 mb-4 rounded-lg bg-error/10 px-3 py-2 text-xs text-error">
            {suggestError}
          </p>
        )}

        {/* Personas from product intelligence */}
        {!graphLoading && unlinkedGraphPersonas.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Brain size={14} className="text-market" />
              <h3 className="font-display text-sm font-semibold text-text-secondary">
                From your product intelligence
              </h3>
              <span className="text-[11px] text-text-muted px-2 py-0.5 bg-market/8 text-market rounded-full">
                {unlinkedGraphPersonas.length}
              </span>
            </div>
            <p className="text-xs text-text-muted mb-3">
              These personas were extracted from your documents. Click to open and enrich them.
            </p>
            <div className="grid grid-cols-3 gap-3">
              {unlinkedGraphPersonas.map(entity => {
                const initials = entity.label
                  .split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase()
                return (
                  <button
                    key={entity.uri}
                    onClick={() => handleOpenGraphPersona(entity)}
                    className="text-left bg-white border border-warm-200 rounded-lg p-3.5 flex items-center gap-3 hover:border-market/40 hover:shadow-sm transition-all group"
                  >
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-market/30 to-market flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
                      {initials}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] font-semibold truncate">{entity.label}</div>
                      <div className="text-[11px] text-market mt-0.5 group-hover:underline">
                        Open in workshop →
                      </div>
                    </div>
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {/* Stakeholder map */}
        {visibleSessions.length > 0 && (
          <StakeholderMap
            sessions={filteredSessions as SessionType[]}
            totalCount={visibleSessions.length}
            typeFilter={typeFilter}
            segmentFilter={segmentFilter}
            segmentOptions={segmentOptions}
            marketPersonas={marketPersonas}
            marketSegments={marketSegments}
            segmentCoverage={segmentCoverage}
            expandedSessionId={expandedSessionId}
            deletingIds={deletingIds}
            syncingIds={syncingIds}
            onTypeFilterChange={setTypeFilter}
            onSegmentFilterChange={setSegmentFilter}
            onToggleExpand={(id) => setExpandedSessionId(current => current === id ? null : id)}
            onOpenFullScreen={handleOpenEditor}
            onResumeWorkshop={(session) => handleOpenSession(session)}
            onSync={handleSync}
            onDelete={handleDeletePersona}
          />
        )}
      </div>

      <ImportPersonaDialog
        accountId={accountId}
        projectId={projectId}
        open={showImport}
        onClose={() => setShowImport(false)}
        onCreated={handleImportCreated}
      />
    </div>
  )
}

interface StakeholderMapProps {
  sessions: SessionType[]
  totalCount: number
  typeFilter: StakeholderTypeFilter
  segmentFilter: string
  segmentOptions: Array<{ id: string; name: string }>
  marketPersonas: MarketPersona[]
  marketSegments: MarketSegmentWithScore[]
  segmentCoverage: SegmentPersonaLink[]
  expandedSessionId: string | null
  deletingIds: Set<string>
  syncingIds: Set<string>
  onTypeFilterChange: (filter: StakeholderTypeFilter) => void
  onSegmentFilterChange: (segmentId: string) => void
  onToggleExpand: (sessionId: string) => void
  onOpenFullScreen: (sessionId: string) => void
  onResumeWorkshop: (session: SessionType) => void
  onSync: (sessionId: string, e: React.MouseEvent) => void
  onDelete: (sessionId: string) => void
}

function StakeholderMap({
  sessions,
  totalCount,
  typeFilter,
  segmentFilter,
  segmentOptions,
  marketPersonas,
  marketSegments,
  segmentCoverage,
  expandedSessionId,
  deletingIds,
  syncingIds,
  onTypeFilterChange,
  onSegmentFilterChange,
  onToggleExpand,
  onOpenFullScreen,
  onResumeWorkshop,
  onSync,
  onDelete,
}: StakeholderMapProps) {
  return (
    <section className="mb-2">
      <div className="mb-2 flex flex-col gap-2 border-y border-border-subtle py-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-text-muted">Stakeholder map</span>
          <span className="text-[11px] text-text-muted tabular-nums">{sessions.length} of {totalCount}</span>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex gap-0.5">
            {(['all', 'user', 'buyer'] as StakeholderTypeFilter[]).map(filter => (
              <button
                key={filter}
                onClick={() => onTypeFilterChange(filter)}
                className={`rounded-md px-3 py-1 text-[12px] font-medium transition-colors ${
                  typeFilter === filter
                    ? 'bg-warm-100 text-text'
                    : 'text-text-muted hover:text-text-secondary'
                }`}
              >
                {typeFilterLabel(filter)}
              </button>
            ))}
          </div>

          <label className="relative inline-flex items-center">
            <Filter className="pointer-events-none absolute left-2.5 h-3.5 w-3.5 text-text-muted" />
            <select
              value={segmentFilter}
              onChange={(e) => onSegmentFilterChange(e.target.value)}
              className="h-7 appearance-none rounded-md bg-transparent pl-7 pr-7 text-[12px] font-medium text-text-secondary outline-none transition-colors hover:bg-warm-100 focus:bg-warm-100"
            >
              <option value="all">All segments</option>
              {segmentOptions.map(segment => (
                <option key={segment.id} value={segment.id}>{segment.name}</option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-2 h-3.5 w-3.5 text-text-muted" />
          </label>
        </div>
      </div>

      {sessions.length === 0 ? (
        <div className="px-8 py-14 text-center">
          <Search className="mx-auto mb-3 h-5 w-5 text-text-muted" />
          <h4 className="font-display text-[16px] font-semibold">No stakeholders match this filter</h4>
          <p className="mt-1 text-sm text-text-muted">Try a different type or applicable segment.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {sessions.map(session => {
            const data = migratePersonaData(session.personaData as PersonaData)
            const segments = getSessionSegments(session, marketPersonas, marketSegments, segmentCoverage)
            const expanded = expandedSessionId === session.id
            return (
              <Fragment key={session.id}>
                <StakeholderPersonaCard
                  session={session}
                  data={data}
                  segments={segments}
                  expanded={expanded}
                  deleting={deletingIds.has(session.id)}
                  syncing={syncingIds.has(session.id)}
                  onToggle={() => onToggleExpand(session.id)}
                  onOpenFullScreen={() => onOpenFullScreen(session.id)}
                  onResumeWorkshop={() => onResumeWorkshop(session)}
                  onSync={(e) => onSync(session.id, e)}
                  onDelete={() => onDelete(session.id)}
                />
                {expanded && (
                  <InlinePersonaReveal
                    session={session}
                    data={data}
                    segments={segments}
                    onCollapse={() => onToggleExpand(session.id)}
                    onOpenFullScreen={() => onOpenFullScreen(session.id)}
                    onResumeWorkshop={() => onResumeWorkshop(session)}
                  />
                )}
              </Fragment>
            )
          })}
        </div>
      )}
    </section>
  )
}

interface StakeholderPersonaCardProps {
  session: SessionType
  data: PersonaData
  segments: SegmentInfo[]
  expanded: boolean
  deleting: boolean
  syncing: boolean
  onToggle: () => void
  onOpenFullScreen: () => void
  onResumeWorkshop: () => void
  onSync: (e: React.MouseEvent) => void
  onDelete: () => void
}

function StakeholderPersonaCard({
  session,
  data,
  segments,
  expanded,
  deleting,
  syncing,
  onToggle,
  onOpenFullScreen,
  onResumeWorkshop,
  onSync,
  onDelete,
}: StakeholderPersonaCardProps) {
  const isBuyer = session.personaType === 'buyer'
  const jtbd = primaryJtbd(data)
  const score = completenessScore(data)
  const pains = totalPains(data.jtbds || [])
  const gains = totalGains(data.jtbds || [])
  const initials = computeInitials(data)
  const status = completenessLabel(data)

  const quoteText = data.quote || jtbd?.title || data.archetype
  const statusDot = score >= 100 ? 'bg-market' : score >= 45 ? 'bg-business' : 'bg-warm-400'

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onToggle}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onToggle()
        }
      }}
      className={`group relative cursor-pointer rounded-xl border bg-bg-card px-4 py-5 text-left transition-all duration-150 ${
        expanded
          ? 'border-border-strong shadow-[0_4px_12px_rgba(29,26,21,0.06)]'
          : 'border-border-subtle shadow-[0_1px_2px_rgba(29,26,21,0.03)] hover:-translate-y-0.5 hover:border-border hover:shadow-[0_4px_12px_rgba(29,26,21,0.05)]'
      }`}
    >
      {/* Header: avatar + identity + buyer/user pill + hover actions */}
      <div className="mb-3 grid grid-cols-[44px_1fr_max-content] items-center gap-3.5">
        <div className={`flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full text-sm font-semibold text-white ${
          data.photo ? 'bg-warm-200' : isBuyer ? 'bg-business' : 'bg-market'
        }`}>
          {data.photo ? <img src={data.photo} alt="" className="h-full w-full object-cover" /> : initials}
        </div>

        <div className="min-w-0">
          <h4 className="truncate font-display text-[16px] font-semibold leading-tight">
            {data.name || data.role || 'New persona'}
          </h4>
          <p className="mt-0.5 truncate text-[13px] text-text-secondary">
            {data.role || 'Role not defined'}
          </p>
        </div>

        <div className="flex items-center gap-1">
          <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium leading-none ${
            isBuyer ? 'bg-business/10 text-business' : 'bg-market/10 text-market'
          }`}>
            <span className="h-1.5 w-1.5 rounded-full bg-current opacity-50" />
            {typeBadge(session)}
          </span>
          <div className="ml-1 flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
            {session.status !== 'completed' && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onResumeWorkshop() }}
                className="rounded-md p-1.5 text-text-muted transition-colors hover:bg-warm-200 hover:text-market"
                title="Resume discovery"
              >
                <ArrowRight className="h-3.5 w-3.5" />
              </button>
            )}
            {!session.personaEntityUri && (
              <button
                type="button"
                onClick={onSync}
                disabled={syncing}
                className="rounded-md p-1.5 text-text-muted transition-colors hover:bg-warm-200 hover:text-market disabled:opacity-40"
                title="Sync to knowledge graph"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${syncing ? 'animate-spin' : ''}`} />
              </button>
            )}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onOpenFullScreen() }}
              className="rounded-md p-1.5 text-text-muted transition-colors hover:bg-warm-200 hover:text-text"
              title="Open full screen"
            >
              <ArrowUpRight className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onDelete() }}
              disabled={deleting}
              className="rounded-md p-1.5 text-text-muted transition-colors hover:bg-error/10 hover:text-error disabled:opacity-40"
              title="Delete persona"
            >
              <Trash2 className={`h-3.5 w-3.5 ${deleting ? 'animate-pulse' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Quote / fallback narrative */}
      {quoteText ? (
        <p className="mb-4 line-clamp-2 max-w-[52ch] text-[13.5px] italic leading-relaxed text-text-secondary">
          <span aria-hidden="true">&ldquo;</span>{quoteText}<span aria-hidden="true">&rdquo;</span>
        </p>
      ) : (
        <p className="mb-4 max-w-[52ch] text-[13px] text-text-muted">
          Click to reveal stakeholder details
        </p>
      )}

      {/* Meta strip: segment chip + stats + completion status */}
      <div className="flex flex-wrap items-center gap-x-3.5 gap-y-2 text-[12px] text-text-muted">
        {segments.length > 0 ? (
          <>
            {segments.slice(0, 2).map(segment => (
              <span key={segment.id} className="inline-flex items-center rounded-full bg-market/10 px-2.5 py-0.5 text-[11.5px] font-medium text-market">
                {segment.name}
              </span>
            ))}
            {segments.length > 2 && (
              <span className="inline-flex items-center rounded-full bg-warm-100 px-2 py-0.5 text-[11px] font-medium text-text-muted">
                +{segments.length - 2}
              </span>
            )}
          </>
        ) : (
          <span className="inline-flex items-center rounded-full bg-warm-100 px-2.5 py-0.5 text-[11.5px] italic text-text-muted">
            No segment
          </span>
        )}

        <span className="text-text-secondary tabular-nums">
          <span className="font-medium text-text">{(data.jtbds || []).length}</span> JTBD ·{' '}
          <span className="font-medium text-text">{pains}</span> pains ·{' '}
          <span className="font-medium text-text">{gains}</span> outcomes
        </span>

        <span className="ml-auto inline-flex items-center gap-1.5">
          <span className={`h-1.5 w-1.5 rounded-full ${statusDot}`} />
          <span>{status} · {timeAgo(session.updatedAt)}</span>
        </span>
      </div>
    </div>
  )
}

interface InlinePersonaRevealProps {
  session: SessionType
  data: PersonaData
  segments: SegmentInfo[]
  onCollapse: () => void
  onOpenFullScreen: () => void
  onResumeWorkshop: () => void
}

function InlinePersonaReveal({
  session,
  data,
  segments,
  onCollapse,
  onOpenFullScreen,
  onResumeWorkshop,
}: InlinePersonaRevealProps) {
  const isBuyer = session.personaType === 'buyer'
  const jtbd = primaryJtbd(data)
  const pains = jtbd?.pains || []
  const gains = jtbd?.gains || []
  const initials = computeInitials(data)
  const jtbdCount = (data.jtbds || []).length
  const totalPainCount = totalPains(data.jtbds || [])
  const totalGainCount = totalGains(data.jtbds || [])

  const severityDot = (severity?: string) =>
    severity === 'high' ? 'bg-error' : severity === 'medium' ? 'bg-solution' : 'bg-warm-400'
  const severityLabel = (severity?: string) =>
    severity === 'high' ? 'Critical' : severity === 'medium' ? 'Moderate' : 'Minor'
  const importanceDot = (importance?: string) =>
    importance === 'must_have' ? 'bg-market' : importance === 'expected' ? 'bg-solution' : 'bg-warm-400'
  const importanceLabel = (importance?: string) =>
    importance === 'must_have' ? 'Critical' : importance === 'expected' ? 'Important' : 'Nice to have'

  return (
    <article className="md:col-span-2 animate-in fade-in-0 duration-200">
      <div className="rounded-xl border border-border-subtle bg-warm-100/60 px-6 py-6">

        {/* Header */}
        <div className="mb-4 grid grid-cols-[56px_1fr_max-content] items-center gap-4">
          <div className={`flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-full text-base font-semibold text-white ${
            data.photo ? 'bg-warm-200' : isBuyer ? 'bg-business' : 'bg-market'
          }`}>
            {data.photo ? <img src={data.photo} alt="" className="h-full w-full object-cover" /> : initials}
          </div>
          <div className="min-w-0">
            <div className="flex items-baseline gap-2">
              <h3 className="font-display text-[22px] font-semibold leading-tight tracking-tight">
                {data.name || 'New persona'}
              </h3>
              {data.archetype && (
                <span className={`truncate font-display text-[13px] italic ${isBuyer ? 'text-business' : 'text-market'}`}>
                  {data.archetype}
                </span>
              )}
            </div>
            <p className="mt-0.5 text-[13px] text-text-secondary">
              {data.role || 'Role not defined'}
            </p>
          </div>
          <div className="flex items-center gap-1">
            <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium leading-none ${
              isBuyer ? 'bg-business/10 text-business' : 'bg-market/10 text-market'
            }`}>
              <span className="h-1.5 w-1.5 rounded-full bg-current opacity-50" />
              {typeBadge(session)}
            </span>
            {session.status !== 'completed' && (
              <button
                type="button"
                onClick={onResumeWorkshop}
                className="ml-1 inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-[12px] font-medium text-text-secondary transition-colors hover:bg-warm-200 hover:text-text"
              >
                Resume <ArrowRight className="h-3 w-3" />
              </button>
            )}
            <button
              type="button"
              onClick={onOpenFullScreen}
              className="inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-[12px] font-medium text-text-secondary transition-colors hover:bg-warm-200 hover:text-text"
            >
              Edit <ArrowUpRight className="h-3 w-3" />
            </button>
            <button
              type="button"
              onClick={onCollapse}
              className="rounded-md p-1.5 text-text-muted transition-colors hover:bg-warm-200 hover:text-text"
              title="Collapse"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        {/* Quote / context */}
        {(data.quote || data.context) && (
          <p className="mb-5 max-w-[60ch] border-l-2 border-market pl-4 font-display text-[15px] italic leading-relaxed text-text">
            {data.quote || data.context}
          </p>
        )}

        {/* Primary JTBD */}
        {jtbd ? (
          <>
            <div className="mb-4">
              <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-text-muted">
                Primary job to be done{jtbdCount > 1 ? ` · 1 of ${jtbdCount}` : ''}
              </div>
              <h3 className="mb-1 font-display text-[18px] font-semibold leading-tight tracking-tight">
                {jtbd.title || 'Untitled job'}
              </h3>
              {jtbd.functional && (
                <p className="max-w-[64ch] text-[13.5px] leading-relaxed text-text-secondary">
                  {jtbd.functional}
                </p>
              )}
            </div>

            {(pains.length > 0 || gains.length > 0) && (
              <div className="grid grid-cols-1 gap-x-10 gap-y-4 border-t border-border-subtle pt-4 md:grid-cols-2">
                <section>
                  <div className="mb-2 flex items-baseline justify-between text-[11px] font-semibold uppercase tracking-[0.12em] text-text">
                    <span>Key pains</span>
                    <span className="font-normal normal-case tracking-normal text-text-muted tabular-nums">{pains.length}</span>
                  </div>
                  <ul className="m-0 list-none space-y-1.5 p-0">
                    {pains.slice(0, 4).map(p => (
                      <li key={p.id} className="grid grid-cols-[10px_1fr_max-content] items-baseline gap-3">
                        <span className={`mt-1 h-1.5 w-1.5 rounded-full ${severityDot(p.severity)}`} />
                        <span className="truncate text-[13px] text-text">{p.description}</span>
                        <span className="text-[10.5px] uppercase tracking-[0.06em] text-text-muted">{severityLabel(p.severity)}</span>
                      </li>
                    ))}
                  </ul>
                  {pains.length > 4 && (
                    <div className="mt-2 text-[11px] text-text-muted">+{pains.length - 4} more</div>
                  )}
                </section>

                <section>
                  <div className="mb-2 flex items-baseline justify-between text-[11px] font-semibold uppercase tracking-[0.12em] text-text">
                    <span>Desired outcomes</span>
                    <span className="font-normal normal-case tracking-normal text-text-muted tabular-nums">{gains.length}</span>
                  </div>
                  <ul className="m-0 list-none space-y-1.5 p-0">
                    {gains.slice(0, 4).map(g => (
                      <li key={g.id} className="grid grid-cols-[10px_1fr_max-content] items-baseline gap-3">
                        <span className={`mt-1 h-1.5 w-1.5 rounded-full ${importanceDot(g.importance)}`} />
                        <span className="truncate text-[13px] text-text">{g.description}</span>
                        <span className="text-[10.5px] uppercase tracking-[0.06em] text-text-muted">{importanceLabel(g.importance)}</span>
                      </li>
                    ))}
                  </ul>
                  {gains.length > 4 && (
                    <div className="mt-2 text-[11px] text-text-muted">+{gains.length - 4} more</div>
                  )}
                </section>
              </div>
            )}

            <div className="mt-5 border-t border-border-subtle pt-4">
              <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-text-muted">User story</div>
              <p className="max-w-[64ch] font-display text-[14px] italic leading-relaxed text-text-secondary">
                {jtbd.customerProblem?.statement
                  || `As ${data.name || data.role || 'this stakeholder'}, I want ${jtbd.title.toLowerCase()} so I can make progress without avoidable friction.`}
              </p>
            </div>
          </>
        ) : (
          <p className="rounded-lg bg-warm-100 px-4 py-6 text-center text-sm italic text-text-muted">
            No primary JTBD yet. Open full screen to add one.
          </p>
        )}

        {/* Footer meta */}
        <div className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-border-subtle pt-3 text-[11px] text-text-muted">
          <span className="tabular-nums">
            <span className="font-medium text-text">{jtbdCount}</span> JTBD ·{' '}
            <span className="font-medium text-text">{totalPainCount}</span> pains ·{' '}
            <span className="font-medium text-text">{totalGainCount}</span> outcomes
          </span>
          {segments.length > 0 && (
            <>
              <span className="h-1 w-1 rounded-full bg-warm-300" />
              <span>
                {segments.slice(0, 2).map(s => s.name).join(', ')}
                {segments.length > 2 && `, +${segments.length - 2}`}
              </span>
            </>
          )}
          <span className="ml-auto">Updated {timeAgo(session.updatedAt)}</span>
        </div>

      </div>
    </article>
  )
}
